/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : Login.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import {
  Avatar,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import PropTypes from "prop-types";
import { AuthTextField } from "../";
import appMeta from "../../assets/app-meta/appMeta";
import { Link } from "react-router-dom";
import CustomButtonV2 from "../customComponentsV2/common/button/CustomButtonV2";

function Login({ throttledHandleLogin, handleAuthViewChange }) {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const handleForgotPasswordOnClick = () => {
    handleAuthViewChange(appMeta.authView.FORGOT_PASSWORD);
  };

  const handleLoginOnClick = () => {
    const trimmedEmail = String(email || "").trim();
    const trimmedPassword = String(password || "").trim();

    const emailRegex = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

    if (emailRegex.test(trimmedEmail)) {
      setEmailError("");
    } else {
      setEmailError("Invalid email address");
      return;
    }

    const passwdMinLength = 8;

    if (trimmedPassword.length >= passwdMinLength) {
      setPasswordError("");
    } else {
      setPasswordError("Invalid password");
      return;
    }

    throttledHandleLogin({
      email: trimmedEmail,
      password: trimmedPassword,
      rememberMe,
    });
  };

  return (
    <Box sx={styles.mainContainer}>
      <Avatar variant="rounded" sx={styles.avatar}>
        <img height={"100%"} src="deloitteIconBlackLarge.png" alt="Deloitte" />
      </Avatar>

      <Typography style={{ fontWeight: 600 }} component="h1" variant="body1">
        Welcome to Digital Compliance
      </Typography>

      <Box sx={styles.fldsContainer}>
        <AuthTextField
          label={"Email"}
          placeHolder={"Email"}
          type={"email"}
          fldType={appMeta.textFieldTypes.EMAIL}
          variant={"standard"}
          value={email}
          setValue={setEmail}
          valueError={emailError}
          setValueError={setEmailError}
          isAutoFocused={true}
          isRequired={true}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleLoginOnClick();
          }}
          txtFldStyle={{
            mt: 1,
            mb: 2,
          }}
        />
        <AuthTextField
          label={"Password"}
          placeHolder={"Password"}
          type={"password"}
          fldType={appMeta.textFieldTypes.AUTHPASSWORD}
          variant={"standard"}
          value={password}
          setValue={setPassword}
          valueError={passwordError}
          setValueError={setPasswordError}
          isRequired={true}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleLoginOnClick();
          }}
          txtFldStyle={{
            mt: 1,
            mb: 2,
          }}
        />
        <Box sx={styles.rmFpContainer}>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  value="remember"
                  color="primary"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
              }
              label="Remember Me"
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <CustomButtonV2
            type="submit"
            btnName="Login"
            handleOnClick={handleLoginOnClick}
          />
        </Box>
        <Box sx={styles.fpLinkContainer}>
          <Link
            style={styles.linkStyle}
            href="#"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              handleForgotPasswordOnClick();
            }}
          >
            Forgot Password?
          </Link>
        </Box>
      </Box>
    </Box>
  );
}

Login.propTypes = {
  throttledHandleLogin: PropTypes.func,
  handleAuthViewChange: PropTypes.func,
};

export default Login;

const styles = {
  mainContainer: {
    width: "100%",
    paddingX: 5,
    paddingY: 4,
    borderRadius: 1,
    backgroundColor: "white",
    border: { sm: "1px solid lightgrey" },
    boxShadow: { sm: "0px 2px 4px rgba(0, 0, 0, 0.1)" },
    boxSizing: "border-box",
  },
  fldsContainer: {
    width: "100%",
    mt: 1,
  },
  linkStyle: {
    color: "#1976d2",
  },
  avatar: {
    mb: 3,
    bgcolor: "white",
    width: "100%",
    height: 24,
  },
  rmFpContainer: {
    display: "flex",
    justifyContent: "start",
    marginTop: 0.5,
    marginBottom: -1.5,
  },
  divider: {
    mb: 2,
  },
  fpLinkContainer: {
    mt: 3,
    mb: 1,
  },
};
